import {
  Component,
  AfterViewInit,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  ElementRef,
  ViewChild,
  HostListener,
  ChangeDetectorRef,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { NgxExtendedPdfViewerService, pdfDefaultOptions } from 'ngx-extended-pdf-viewer';
import { AnswerModel } from 'src/app/models/answer.model';
import { DocumentModel } from 'src/app/models/document.model';
import { FileMenuDialogComponent } from '../dialogs/file-menu-dialog/file-menu-dialog.component';
import {
  DocumentActionClose,
  DocumentActionDownload,
  DocumentActionDownloadAsPdf,
  DocumentActionLogout,
  DocumentActionModel,
  DocumentActionNew,
  DocumentActionOpen,
  DocumentActionPrint,
  DocumentActionSave,
  DocumentActionSaveAs,
  DocumentActionUpload,
} from 'src/app/models/document-action.model';
import { PdfDocumentService } from 'src/app/services/pdf-document.service';
import {
  Observable,
  Subject,
  debounceTime,
  finalize,
  firstValueFrom,
  from,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { UnsavedChangesDialogComponent } from '../dialogs/unsaved-changes-dialog/unsaved-changes-dialog.component';
import { OnlineClientService } from 'src/app/services/online-client.service';
import { AutosaveService } from 'src/app/services/autosave.service';
import { ToastService } from 'src/app/services/toast.service';
import { AnswersChangedDialogComponent } from '../dialogs/answers-changed-dialog/answers-changed-dialog.component';
import { FileMenuActions } from 'src/app/models/file-menu.model';
import { DebugDialogComponent } from '../dialogs/debug-dialog/debug-dialog.component';
import { AutosaveModel } from 'src/app/models/autosave.model';
import { RecoverDocumentsDialogComponent } from '../dialogs/recover-documents-dialog/recover-documents-dialog.component';
import { AppInsightsService } from 'src/app/services/app-insights.service';
import { Location } from '@angular/common';
import { WindowService } from 'src/app/services/window.service';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { DialogService } from 'src/app/services/dialog.service';
import { DocumentDataService } from 'src/app/services/document-data.service';
import { LoadingAction } from 'src/app/models/loading-action.model';

import { SessionService } from 'src/app/services/session.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DefaultValueAccessor } from '@angular/forms';
import { AnswerLockService } from 'src/app/services/answer-lock.service';
import { LoadingService } from 'src/app/services/loading.service';
import { FieldDetails, FormField } from 'src/app/services/text-control.service.interface';
import { cloneDeep } from 'lodash';
import { environment } from 'src/environments/environment';
import { IntroductionDialogComponent } from '../dialogs/introduction-dialog/introduction-dialog.component';
import { PreferencesService } from 'src/app/services/preferences.service';
import { PendingTakeoverSessionsDialogComponent } from '../dialogs/pending-takeover-sessions-dialog/pending-takeover-sessions-dialog.component';
declare const globalThis: any;
declare const PDFViewerApplication: any;
interface GetAnswerPoolResponse {
  answers: any[];
}

@Component({
  selector: 'tx-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [OnlineClientService],
  animations: [
    trigger('fadeInOut', [
      state('in', style({ opacity: 1 })),
      transition(':enter', [
        // :enter is alias to 'void => *'
        style({ opacity: 0 }),
        animate(600),
      ]),
      transition(':leave', [
        // :leave is alias to '* => void'
        animate(600, style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class PdfViewerComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  public document: DocumentModel;
  public isDirty: boolean;
  public base64Doc: string = '';
  public initials: string;
  public onFocusHandler: any;
  public onPageHideHandler: any;
  public onBlurHandler: any;
  public onKeyDownHandler: any;
  public updatingAnswersFromPool: boolean;
  public answerData: { [fieldName: string]: any };
  public isRecovered: boolean;
  public isLoading: boolean = true;
  private documentEventLogName = 'DocumentAction';
  private formData$: Subject<any> = new Subject<any>();
  private destroy$ = new Subject<void>();
  public zoom: string | number = '100%';
  public isFileMenuOpen: boolean = false;
  public showTXdocsRibbonBar: boolean = false;
  private refreshSessionId: string = '';
  public refreshDcmId: string = '';
  public activeSelectedField: FormField | undefined;

  public hasJSError: boolean = false;
  private documentAnswersMap: Map<string, any> = new Map<string, any>();
  private hasPendingTakeoverSessions: boolean = false;

  public loadingAction: LoadingAction = LoadingAction.Loading;
  @ViewChild('fileButton') fileButton!: ElementRef;

  get jsonData(): any {
    // console.log('answerData', this.answerData);
    return JSON.stringify(this.answerData, null, 2);
  }

  get documentDisconnectedFromTXdocsAnswerPool(): boolean {
    return this.pdfDocumentService.documentDisconnectedFromTXdocsAnswerPool;
  }

  set jsonData(value: any) {
    try {
      this.answerData = JSON.parse(value);
    } catch (e) {
      // Handle the error if the JSON is invalid
      console.error('Invalid JSON:', e);
    }
  }
  constructor(
    private pdfService: NgxExtendedPdfViewerService,
    private offCanvasService: NgbOffcanvas,
    private pdfDocumentService: PdfDocumentService,
    private authenticationService: AuthenticationService,
    private onlineClientService: OnlineClientService,
    private autosaveService: AutosaveService,
    private toastService: ToastService,
    private logger: AppInsightsService,
    private location: Location,
    private windowService: WindowService,
    private changeDetector: ChangeDetectorRef,
    private dialogService: DialogService,
    private documentDataService: DocumentDataService,
    private sessionService: SessionService,
    private answerLockService: AnswerLockService,
    public loadingService: LoadingService,
    private preferencesService: PreferencesService
  ) {
    pdfDefaultOptions.doubleTapZoomFactor = '150%'; // The default value is '200%'
    pdfDefaultOptions.maxCanvasPixels = 4096 * 4096 * 5; // The default value is 4096 * 4096 pixels,
    pdfDefaultOptions.enableXfa = true;
    pdfDefaultOptions.enableScripting = true;
    //Temporary fix for the issue with the pdf viewer is trying to load sandbox.###.es5.min.mjs
    pdfDefaultOptions._internalFilenameSuffix = '';
    this.isDirty = false;
    this.document = {
      isActiveDocument: true,
      saveAsPdf: true,
      answersChanged: false,
      openInAnotherSession: false,
      answers: [],
      pdfAnswersChanged: false,
      txdocsAnswersChanged: false,
    };
    this.updatingAnswersFromPool = false;
    this.initials = authenticationService.getInitials();
    this.onFocusHandler = this.onFocus.bind(this);
    this.onPageHideHandler = this.onPageHide.bind(this);
    this.onBlurHandler = this.onBlur.bind(this);
    this.onKeyDownHandler = this.onKeydown.bind(this);
    this.answerData = {};
    this.isRecovered = false;
    globalThis['ngxConsoleFilter'] = (level: string, message: any): boolean => {
      if (level == 'error') {
        console.error('PDFJS ERROR:', message);
        logger.logException(new Error('PDFJS ERROR:' + message), SeverityLevel.Critical);
        return false;
      }
      return true;
    };
  }

  ngOnInit() {
    let currentUrl = this.location.path().split('?')[0];
    this.location.replaceState(currentUrl);
    const navigationEntries: any = performance.getEntriesByType('navigation');
    if (navigationEntries.length > 0) {
      const navigationEntry = navigationEntries[0];
      if (navigationEntry.type === 'reload') {
        console.log('==>Refreshed the BROWSER');
        this.refreshSessionId = sessionStorage.getItem('refresh_sessionId') ?? '';
        this.refreshDcmId = sessionStorage.getItem('refresh_dcmId') ?? '';
        console.log('==> refreshSessionId', this.refreshSessionId);
      } else {
        console.log('==> NO REFRESH');
      }
      sessionStorage.removeItem('refresh_sessionId');
      sessionStorage.removeItem('refresh_dcmId');
    }
  }

  ngAfterViewInit(): void {
    this.sessionService.checkLicenseCount();
    const sesionId = this.windowService.getSessionIdFromWindow();
    this.addWindowEventListeners();
    globalThis['ngxConsoleFilter'] = (level: string, message: any): boolean => {
      if (level.toLowerCase() == 'error') {
        this.hasJSError = true;

        console.log(`PDFJS ${level}:`, message);
        this.logger.logException(new Error('PDFJS ERROR:' + message), SeverityLevel.Critical, {
          txDocs_DocumentId: this.document.dcmID,
          txDocs_TemplateID: this.document.templateId,
        });
        return false;
      }
      return true;
    };

    const recoverSessionId = sessionStorage.getItem('recover_autosave') ?? null;
    const resumeSessionId = sessionStorage.getItem('resume_sessionId') ?? null;
    if (recoverSessionId) {
      this.getAutosaveBySessionId(recoverSessionId);
    } else if (this.refreshSessionId) {
      this.resumeSessionById(this.refreshSessionId, this.refreshDcmId);
    } else if (sesionId) {
      if (resumeSessionId) {
        this.resumeSessionById(sesionId, '');
      } else {
        this.getDocumentBySession(this.document);
      }
    }

    //TODO: THIS IS NOT THE RIGHT SPOT FOR THIS EXECUTION - hasJSErorr will always be false by the time we get here
    //We will show the dialog after checking if the answers have changed regardless of an internal JS error for now
    // console.log(`this.hasJSError =`, this.hasJSError);
    // if (!this.hasJSError) {
    //   this.document.txdocsAnswersChanged
    //     ? this.openAnswersChangedDialog()
    //     : window.addEventListener('focus', this.onFocusHandler);
    // }
  }

  onPdfLoadedHandler() {
    //console.log(`inside PDF loading handler`);
    this.onPdfLoaded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('ngOnChanges', changes);
  }

  ngOnDestroy(): void {
    this.removeWindowEventListeners();
    this.dialogService.closeDialogs();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.document.dcmID && !this.isRecovered) {
      sessionStorage.setItem('refresh_dcmId', this.document.dcmID);
    }
    if (this.isDirty) {
      this.autosaveService.updateBeforeUnloadBeacon();
      // CUSTOM MESSAGES ARE NOT POSSIBLE, BUT WE CAN STILL FORCE THE BROWSER TO SHOW THE CUSTOM MESSAGE
      // https://developer.chrome.com/blog/chrome-51-deprecations/#remove-custom-messages-in-onbeforeunload-dialogs
      const confirmationMessage = 'There are unsaved changes.  Are you sure you want to leave this page?';
      event.returnValue = confirmationMessage;
      return confirmationMessage;
    } else if (this.hasPendingTakeoverSessions) {
      const confirmationMessage =
        'There are documents from the same case waiting to be taken over. If you don\t take them over you will lose any changes. Are you sure you want to leave this page?';
      event.returnValue = confirmationMessage;
      return confirmationMessage;
    }
    globalThis.PDFViewerApplication.pdfDocument.annotationStorage.resetModified();
    return undefined;
  }

  public get formData(): { [fieldName: string]: any } {
    return this.answerData;
  }

  public set formData(data: { [fieldName: string]: any }) {
    this.answerData = data;
  }

  onFormDataChange(event: any) {
    this.formData$.next(event);
  }

  onFileClicked(preselectedComponent: string = '') {
    if (this.isFileMenuOpen) return;
    this.isFileMenuOpen = true;
    this.showTXdocsRibbonBar = false;
    this.changeDetector.detectChanges();

    const offCanvasRef = this.offCanvasService.open(FileMenuDialogComponent, {
      panelClass: 'file-menu-panel',
      backdropClass: 'file-menu-backdrop',
    });

    offCanvasRef.componentInstance.document = this.document;
    offCanvasRef.componentInstance.isPdf = true;
    offCanvasRef.componentInstance.preselectedComponent = preselectedComponent;
    offCanvasRef.componentInstance.isDirty = this.isDirty;

    offCanvasRef.result
      .then((res: DocumentActionModel) => {
        if (res && res.action) {
          switch (res.action) {
            case DocumentActionNew:
              break;
            case DocumentActionSave:
              if (this.isRecovered) {
                this.onFileClicked(FileMenuActions.SaveAs);
              } else {
                this.save();
              }
              break;
            case DocumentActionSaveAs:
              this.saveAs();
              break;
            case DocumentActionOpen:
              if (res.document) {
                this.open(res.document);
              }
              break;
            case DocumentActionPrint:
              this.printDocument();
              break;
            case DocumentActionDownload:
            case DocumentActionDownloadAsPdf:
              this.downloadDocument();
              break;
            case DocumentActionUpload:
              if (res.document) {
                this.logger.logEvent(this.documentEventLogName, {
                  method: 'onFileClicked->ActionUpload',
                  document: res.document,
                });
                this.uploadDocument(res.document);
              }
              break;
            case DocumentActionLogout:
              this.logout();
              break;
            case DocumentActionClose:
              this.closeDocument();
              break;
          }
        }
      })
      .finally(() => {
        setTimeout(() => {
          // set focus to editor
          this.fileButton?.nativeElement?.blur();
          this.isFileMenuOpen = false;
          document.getElementById('viewer')?.focus();
        }, 100);
      });
  }

  public save() {
    const answersLocked = this.answerLockService.getAnswerLock(this.document.casID);
    if (answersLocked && answersLocked.sessionId !== this.windowService.getSessionIdFromWindow()) {
      return;
    }
    this.answerLockService.createAnswerLock(this.document.casID);
    this.saveFormDataToAnswers();

    return this.saveAsObservable()
      .pipe(
        take(1),
        finalize(() => {
          this.answerLockService.removeAnswerLock(this.document.casID);
        })
      )
      .subscribe({
        next: (res: any) => {
          this.toastService.show('Document successfully saved', {
            classname: 'bg-success',
            autohide: true,
          });
        },
        error: (error) => {},
        complete: () => {
          globalThis.PDFViewerApplication.pdfDocument.annotationStorage.resetModified();
          this.isDirty = false;
          this.resetLoadingDocumentIndicator();
        },
      });
  }

  public saveAs() {
    const answersLocked = this.answerLockService.getAnswerLock(this.document.casID);
    if (answersLocked && answersLocked.sessionId !== this.windowService.getSessionIdFromWindow()) {
      return;
    }
    this.answerLockService.createAnswerLock(this.document.casID);

    this.saveFormDataToAnswers();
    this.document.dcmID = '';
    return this.saveAsObservable()
      .pipe(
        take(1),
        finalize(() => {
          this.answerLockService.removeAnswerLock(this.document.casID);
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res && res.dcmID) {
            this.toastService.show('Document successfully saved', {
              classname: 'bg-success',
              autohide: true,
            });
            this.document.dcmID = res.dcmID;
            this.deleteAutosave();
          }
        },
        error: (error) => {},
        complete: () => {
          globalThis.PDFViewerApplication.pdfDocument.annotationStorage.resetModified();
          this.isDirty = false;
        },
      });
  }

  public async uploadDocument(document: DocumentModel) {
    this.pdfDocumentService
      .saveDocument(document)
      .pipe(take(1)) // Automatic Unsubscribe after one received
      .subscribe({
        next: (res) => {
          if (res) {
            this.toastService.show('Document successfully uploaded', {
              classname: 'bg-success',
              autohide: true,
            });
            this.document = res;
            if (res.document) {
              this.formData = {};
              this.base64Doc = res.document;
            }
          }
        },
        error: (err) => {},
        complete: () => {},
      });
  }

  public onBlur() {
    const answersLocked = this.answerLockService.getAnswerLock(this.document.casID);
    if (answersLocked && answersLocked.sessionId !== this.windowService.getSessionIdFromWindow()) {
      return;
    }

    this.answerLockService.createAnswerLock(this.document.casID);
    this.saveFormDataToAnswers();
    let changedAnswers = new Array<AnswerModel>();
    if (this.document.answers && this.document.isActiveDocument) {
      changedAnswers = this.document.answers.filter((a) => a.oldAnswer !== a.answer);
      this.pdfDocumentService
        .saveAnswersToPool(changedAnswers)
        .pipe(
          take(1),
          finalize(() => {
            this.answerLockService.removeAnswerLock(this.document.casID);
          })
        )
        .subscribe((res) => {});
    } else {
      this.answerLockService.removeAnswerLock(this.document.casID);
    }
  }

  public onFocus() {
    const answersLocked = this.answerLockService.getAnswerLock(this.document.casID);
    if (answersLocked) {
      this.loadingService.loading = true;
      this.loadingService.loadingAction = LoadingAction.Updating;
      this.changeDetector.detectChanges();
    }
    this.tryGettingAnswersFromPool();
  }

  public onPdfLoaded() {
    this.checkShowIntroDialog();
    this.subscribeToFormDataChange();
    this.getAutosavesByCasId();
    document.title = this.document.filename ?? environment.name;
    this.pdfDocumentService.documentWasLoaded(this.document);

    //FUTURE FEATURE will allow for the subscriber to have the PDFdocument answers loaded into their case by adding (casID+firmGUID) to the hash to prevent pollution
    //this bool represents a difference in the "TXdocsAnswerVersion"
    if (this.document.pdfAnswersChanged) {
      //for now - this answer version hash is law - if it is determined to be different we will disconnect the document automatically
      this.toastService.show(
        'This document has been modified outside of TXdocs. The answers in this document will not be saved to TXdocs',
        { classname: 'bg-warning', autohide: true }
      );
      this.disconnectDocumentFromPool();
    } else if (!this.document.isActiveDocument) {
       // don't check for changed answers if it's an inactive/recovered document
      this.disconnectDocumentFromPool();
      return;
    } else {
      //are the document answers different from the answerPool answers? - are there fields at all?
      this.compareDocumentAnswersWithAnswerPool().then((result) => {
        console.log(`TXdocsAnswersChanged result from comparing =`, result);
        if (result.txdocsAnswersChanged) {
          this.openAnswersChangedDialog();
        } else {
          this.document.answers = this.setOldAnswers(this.document.answers ?? []);
          window.addEventListener('focus', this.onFocusHandler);
        }
        if (!result.hasFields) {
          console.log(`FOUND NO FIELDS - DOCUMENT DISCONNECTED`);
          this.disconnectDocumentFromPool();
        }
      });
    }
  }

  disconnectDocumentFromPool() {
    this.pdfDocumentService.setDocumentDisconnected(true);
    this.document.isActiveDocument = false;
    this.onlineClientService.disconnectSessionAndKeepDocument();
  }

  async compareDocumentAnswersWithAnswerPool(): Promise<{ txdocsAnswersChanged: boolean; hasFields: boolean }> {
    //assign defaults
    let txdocsAnswersChanged = true;
    let hasFields = false;
    try {
      //get pool answers - extra api call, TODO :: flatten logic or ensure everything is loaded from here after we are done
      const obs = this.pdfDocumentService.getAnswersFromPool(this.document);
      const postResponse = await firstValueFrom(obs);
      const answerPoolAnswers = postResponse as unknown as GetAnswerPoolResponse;
      //alter the structure to match our document answers array for easy comparison
      const tokensAndValuesFromAnswers: any[] = [];
      if (Array.isArray(answerPoolAnswers.answers)) {
        answerPoolAnswers.answers.forEach((answer) => {
          if (answer.dimIndex && answer.dimIndex.trim() !== '') {
            const indices = answer.dimIndex.split(',');
            indices.forEach((index: any) => {
              tokensAndValuesFromAnswers.push({
                name: `${answer.token.slice(0, -1)}[${index}]${answer.token.slice(-1)}_`,
                value: answer.answer,
              });
            });
          } else {
            tokensAndValuesFromAnswers.push({ name: `${answer.token}_`, value: answer.answer });
          }
        });
      } else {
        this.logger.logException(
          new Error('Answer compare error - The property "answers" is not an array or does not exist.'),
          SeverityLevel.Critical
        );
      }

      //get document answers
      const documentAnswers: any = [];
      for (let pageNum = 1; pageNum <= PDFViewerApplication.pdfDocument.numPages; pageNum++) {
        await PDFViewerApplication.pdfDocument.getPage(pageNum).then(async function (page: any) {
          // Get annotations for the page
          await page.getAnnotations().then(function (this: PdfViewerComponent, annotations: any) {
            annotations.forEach(function (this: PdfViewerComponent, annotation: any) {
              //Check if the annotation is a form field - Widget is all the varios user input types (checkbox, radio, text, dropdowns)
              if (annotation.subtype === 'Widget') {
                hasFields = true;
                //alter radio/checkbox fields
                if (annotation.fieldType === 'Btn') {
                  if (annotation.radioButton === true) {
                    if (annotation.fieldValue == null) {
                      documentAnswers.push({ name: annotation.fieldName, value: 'Off' });
                    }
                  }
                  if (annotation.checkBox === true) {
                    if (annotation.fieldValue == 'Off') {
                      documentAnswers.push({ name: annotation.fieldName, value: '0' });
                    }
                  }
                } else {
                  //if the field is numeric and unanswered the poolValue will be "0" - the document value will be null --- (alter doc value to 0 if null or '')
                  let modifiedFieldValue = annotation.fieldValue;
                  if (annotation.actions != null && (annotation.actions.Format || annotation.actions.Keystroke)) {
                    if (
                      annotation.actions.Format.some((e: string) => e.includes('Number')) ||
                      annotation.actions.Keystroke.some((e: string) => e.includes('Number'))
                    ) {
                      modifiedFieldValue =
                        annotation.fieldValue === '' || annotation.fieldValue === null ? '0' : annotation.fieldValue;
                    }
                  }
                  //if answer is  still '' use null to match pool
                  documentAnswers.push({
                    name: annotation.fieldName,
                    value: modifiedFieldValue === '' ? null : modifiedFieldValue,
                  });
                }
              }
            });
          });
        });
      }

      //compare and determine if the answers are out of sync
      const differences = [];
      //immutable maps
      const poolAnswersMap = new Map(tokensAndValuesFromAnswers.map((item: any) => [item.name, item.value]));
      this.documentAnswersMap = new Map(documentAnswers.map((item: any) => [item.name, item.value]));

      for (const [name, poolValue] of poolAnswersMap) {
        if (this.documentAnswersMap.has(name)) {
          const documentValue = this.documentAnswersMap.get(name) as string;
          if ((poolValue ? poolValue.trim() : '') !== (documentValue ? documentValue.trim() : '')) {
            //remove any empty spaces to ensure accurate string comparisons
            differences.push({
              name,
              poolValue,
              documentValue,
            });
          }
        }
      }

      console.log('Differences:', differences);
      // console.log('DocumentAnswers:', documentAnswers);
      // console.log('tokensAndValues:', tokensAndValuesFromAnswers);
      //set changed flag
      if (differences.length > 0) {
        txdocsAnswersChanged = true;
      } else {
        txdocsAnswersChanged = false;
      }

      return { txdocsAnswersChanged, hasFields };
    } catch (error) {
      this.logger.logException(new Error('Answer compare error - ' + error), SeverityLevel.Critical);
    }
    //return defaults - safety
    return { txdocsAnswersChanged, hasFields };
  }

  open(document: DocumentModel) {
    this.pdfDocumentService
      .getDocument(document)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoading = false;
          this.changeDetector.detectChanges();
        })
      )
      .subscribe({
        next: (res: DocumentModel) => {
          if (res && res.document) {
            this.document = res;
            this.base64Doc = res.document;
          }
        },
        error: () => {
          this.onFileClicked();
        },
      });
  }

  private getDocumentBySession(document: DocumentModel) {
    const sessionId = this.windowService.getSessionIdFromWindow();
    if (sessionId != null && sessionId !== '') {
      this.document.sessionId = sessionId;
      this.pdfDocumentService
        .getDocumentBySession(document)
        .pipe(
          take(1),
          finalize(() => {
            this.isLoading = false;
            this.changeDetector.detectChanges();
          })
        )
        .subscribe({
          next: (res: DocumentModel) => {
            if (res && res.document) {
              this.document = res;
              this.base64Doc = res.document;
              this.documentDataService.setCasId(this.document.casID ?? '');
              this.documentDataService.setCtcId(this.document.ctcID ?? '');
            }
          },
          error: () => {
            this.onFileClicked();
          },
        });
    }
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        resolve(base64String.split(',')[1]); // Extracting only the base64 string
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(blob);
    });
  }

  public async logout() {
    this.checkUnsavedChanges()
      .pipe(take(1))
      .subscribe((res: any) => {
        if (res === true) {
          this.onlineClientService
            .disconnect() // dont logout yet cause the logout below will take care of it.
            .pipe(take(1))
            .subscribe((res) => {
              this.removeWindowEventListeners();
              this.autosaveService
                .delete(this.windowService.getSessionIdFromWindow() ?? '')
                .pipe(take(1))
                .subscribe((res) => {
                  this.authenticationService.logout();
                });
            });
        } else {
          this.toastService.show('You have unsaved changes. Please save the document before you logout.', {
            classname: 'bg-warning',
            autohide: true,
          });
        }
      });
  }

  public onKeydown(e: any) {
    if (e.ctrlKey && e.shiftKey && e.which === 68) {
      //"D"
      e.preventDefault();
      this.openDebugDialog();
      e.stopPropagation();
    } else if (e.ctrlKey && e.shiftKey && e.which === 74) {
      //"J" prevent page break
      e.preventDefault();
      e.stopPropagation();
    } else if (e.ctrlKey && e.shiftKey && e.which === 86) {
      //"V" handle no format pasting
      e.preventDefault();
      this.triggerPasteEvent(e);
    } else if (e.ctrlKey) {
      this.handleCtrlKeyShortcuts(e);
    }
  }

  private handleCtrlKeyShortcuts(e: any) {
    switch (e.which) {
      case 79: // ctrl + 0
        e.preventDefault();
        this.onFileClicked(FileMenuActions.Open);
        e.stopPropagation();
        break;
      case 80: // ctrl + p
        e.preventDefault();
        this.printDocument();
        e.stopPropagation();
        break;
      case 83: // ctrl + s
        e.preventDefault();
        if (this.isRecovered) {
          this.onFileClicked(FileMenuActions.SaveAs);
        } else {
          //set loading
          this.loadingService.loading = true;
          this.loadingService.loadingAction = LoadingAction.Saving;
          //TODO: bug exists with the document being closed before the save occurs - we need to investigate pdf js and see if we can
          //grok when the field values are updated and only call save when its not "working"
          this.save();
        }
        e.stopPropagation();
        break;
      default:
        break;
    }
  }

  private triggerPasteEvent(e: any) {
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard
      .readText()
      .then((text) => {
        const pasteEvent = new ClipboardEvent('paste', {
          clipboardData: new DataTransfer(),
        });
        pasteEvent?.clipboardData?.setData('text/plain', text);
        const targetElement = document.activeElement;
        if (targetElement) {
          targetElement.dispatchEvent(pasteEvent);
        }
      })
      .catch((error) => {
        console.error('Failed to read clipboard text:', error);
      });
  }

  public updateZoomFactor(zoom: number | string) {
    if (isNaN(Number(zoom))) {
      this.zoom = zoom;
    } else {
      this.zoom = `${zoom}%`;
    }
  }

  openDebugDialog() {
    const debugDialogRef = this.dialogService.openCustomDialog('debug', DebugDialogComponent);
    debugDialogRef.componentInstance.firmId = this.authenticationService.getFirmId();
    debugDialogRef.componentInstance.sessionId = this.windowService.getSessionIdFromWindow();
    debugDialogRef.componentInstance.contactId = this.document.ctcID;
    debugDialogRef.componentInstance.caseId = this.document.casID;
    debugDialogRef.componentInstance.documentId = this.document.dcmID;
    debugDialogRef.componentInstance.answerPoolId = this.document.answerPoolId;
    debugDialogRef.componentInstance.appInsightsSessionId = this.authenticationService.getAISession();
    debugDialogRef.componentInstance.selectedField = this.activeSelectedField;
  }

  private retrieveKeyOfAnswerMapItemByAnswerObject(answer: AnswerModel): string {
    let key = answer.token + '_';
    if (answer.dimIndex && answer.dimIndex !== '') {
      key = `${answer.token.slice(0, 6)}[${answer.dimIndex}]${answer.token.slice(-1)}_`;
    }
    return key;
  }

  private saveAsObservable(): Observable<any> {
    // because the recovered document still has the id of the original document, we set it to empty string to save it as a new document
    // we don't want to overwrite the original document
    this.changeDetector.detectChanges();
    if (this.isRecovered) {
      this.document.dcmID = '';
    }
    let localDocument = cloneDeep(this.document);

    localDocument.answers = localDocument.answers?.filter((e) => {
      let key = this.retrieveKeyOfAnswerMapItemByAnswerObject(e);
      return this.documentAnswersMap.has(key) && e.answer !== null && e.token.startsWith('Q');
    });

    return from(this.pdfService.getCurrentDocumentAsBlob()).pipe(
      switchMap((blob) => from(this.blobToBase64(blob))),
      tap((base64) => (this.document.document = base64)),
      switchMap((base64) => {
        localDocument.document = base64;
        return this.pdfDocumentService.saveDocument(localDocument);
      }),
      take(1),
      tap(() => {
        globalThis.PDFViewerApplication.pdfDocument.annotationStorage.resetModified();
        this.isDirty = false;
      })
    );
  }

  public checkUnsavedChanges(): Observable<boolean> {
    if (this.isDirty) {
      var saved = new Subject<boolean>();
      const unsavedChangesDialogRef = this.dialogService.openCustomDialog(
        'unsavedChanges',
        UnsavedChangesDialogComponent,
        {
          backdrop: 'static',
        }
      );

      unsavedChangesDialogRef.result.then((res: boolean) => {
        if (res === true) {
          this.loadingService.loading = true;
          this.loadingService.loadingAction = LoadingAction.Saving;
          this.saveAsObservable()
            .pipe(take(1))
            .subscribe({
              next: (res: any) => {
                if (res === false) {
                  saved.next(false);
                } else {
                  saved.next(true);
                }
              },
              error: (err: any) => saved.next(false),
              complete: () => {
                this.loadingService.loading = false;
                this.loadingService.loadingAction = LoadingAction.None;
                saved.complete();
              } 
            });
        } else {
          saved.next(true);
        }
      });
      return saved.asObservable();
    }
    //true means there are no unsaved changes, so we can proceed with the next action
    return of(true);
  }

  //form data has two way binding - objects - "answerData" to compare with answers from pool
  private getAnswersFromPool() {
    this.pdfDocumentService
      .getAnswersFromPool(this.document)
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          if (res && res.answers) {
            this.document.answers = this.setOldAnswers(res.answers);

            this.updatingAnswersFromPool = true;
            this.updateAnswers(res.answers)
              .pipe(
                take(1),
                tap((res) => console.log('setting answers from pool'))
              )
              .subscribe((innerRes) => {
                //returns true when an answer is changed
                if (innerRes) {
                  this.isDirty = true;
                  this.pdfService
                    .getCurrentDocumentAsBlob()
                    .then((blob) => this.blobToBase64(blob))
                    .then((base64) => {
                      this.document.document = base64;
                      if (this.isDirty && this.loadingService.loadingAction !== LoadingAction.Saving) {
                        this.autosaveService.create(this.document, '', '').pipe(take(1)).subscribe();
                      }
                    });
                }
                this.updatingAnswersFromPool = false;
                this.resetLoadingDocumentIndicator();
              });
          }
        },
        error: (error) => {
          this.logger.logException(
            new Error('unable to get PDF answers from pool:' + error.message),
            SeverityLevel.Critical,
            {
              txDocs_DocumentId: this.document.dcmID,
              txDocs_TemplateID: this.document.templateId,
            }
          );
        },
      });
  }

  private tryGettingAnswersFromPool() {
    const currentTimestamp = Date.now();
    console.debug('tryGettingAnswersFromPool', currentTimestamp);
    const answersLocked = this.answerLockService.getAnswerLock(this.document.casID);
    if (answersLocked == null) {
      this.getAnswersFromPool();
    } else {
      this.loadingService.loading = true;
      this.loadingService.loadingAction = LoadingAction.Updating;
      this.changeDetector.detectChanges();
      if (answersLocked.sessionId === this.windowService.getSessionIdFromWindow()) {
        this.answerLockService.removeAnswerLock(this.document.casID);
        this.getAnswersFromPool();
      } else {
        // retry
        setTimeout(() => this.tryGettingAnswersFromPool(), 5);
      }
    }
  }

  private updateAnswers(answers: Array<AnswerModel>): Observable<boolean> {
    if (answers) {
      return of(this.updateFormDataFromAnswers(answers));
    }

    return of(false);
  }

  private onPageHide(e: PageTransitionEvent) {
    // the page is entering the bfcache and may be restored,
    // so don't tear the session down
    if (e.persisted) {
      return;
    }

    const answersLocked = this.answerLockService.getAnswerLock(this.document.casID);
    if (answersLocked && answersLocked.sessionId === this.windowService.getSessionIdFromWindow()) {
      this.answerLockService.removeAnswerLock(this.document.casID);
    }
    // don't send disconnect request if inside of an iframe
    // we don't want the session logged out when the iframe is removed
    if (window.self === window.top) {
      this.onlineClientService.disconnectWithLogout();
    }
  }

  private addWindowEventListeners() {
    window.addEventListener('blur', this.onBlurHandler);
    window.addEventListener('pagehide', this.onPageHideHandler);
    window.addEventListener('keydown', this.onKeyDownHandler);
  }

  private removeWindowEventListeners() {
    window.removeEventListener('focus', this.onFocusHandler);
    window.removeEventListener('blur', this.onBlurHandler);
    window.removeEventListener('pagehide', this.onPageHideHandler);
    window.removeEventListener('keydown', this.onKeyDownHandler);
  }

  public async downloadDocument() {
    this.isLoading = true;
    this.changeDetector.detectChanges();
    this.pdfService
      .getCurrentDocumentAsBlob()
      .then((blob) => {
        if (blob) {
          this.blobToBase64(blob).then((base64) => {
            var element = document.createElement('a');
            element.setAttribute('href', 'data:application/pdf;base64,' + base64);
            element.setAttribute('download', this.document.filename ?? 'download.pdf');
            element.style.display = 'none';
            document.body.appendChild(element);

            // simulate click
            element.click();

            // remove the link
            document.body.removeChild(element);
          });
        }
      })
      .finally(() => {
        //remove the flag
        this.isLoading = false;
        this.changeDetector.detectChanges();
      });
  }

  private subscribeToFormDataChange() {
    this.formData$.pipe(takeUntil(this.destroy$), debounceTime(1000)).subscribe((formData) => {
      this.isDirty = true;
      this.saveFormDataToAnswers();
      let changedAnswers = new Array<AnswerModel>();
      if (this.document.answers && this.document.isActiveDocument) {
        changedAnswers = this.document.answers.filter((a) => a.oldAnswer !== a.answer);
        this.pdfDocumentService
          .saveAnswersToPool(changedAnswers)
          .pipe(take(1))
          .subscribe((res) => {});
      }
    });

    this.formData$.pipe(takeUntil(this.destroy$), debounceTime(3000)).subscribe((res) => {
      this.pdfService
        .getCurrentDocumentAsBlob()
        .then((blob) => this.blobToBase64(blob))
        .then((base64) => {
          this.document.document = base64;
          if (this.isDirty && this.loadingService.loadingAction !== LoadingAction.Saving) {
            this.autosaveService
              .create(this.document, '', sessionStorage.getItem('recover_autosave') ?? '')
              .pipe(take(1))
              .subscribe();
          }
        });
    });
  }

  private openAnswersChangedDialog() {
    const newCaseAnswersDialogRef = this.dialogService.openCustomDialog(
      'newCaseAnswers',
      AnswersChangedDialogComponent,
      {
        backdrop: 'static',
      }
    );
    newCaseAnswersDialogRef.result.then((res) => {
      if (res === true && this.document.answers) {
        this.isDirty = true;
        window.addEventListener('focus', this.onFocusHandler);
        this.getAnswersFromPool();
      } else {
        // The document becomes inactive, so we disconnect it from the answer pool
        this.onlineClientService.disconnectSessionAndKeepDocument();
        this.document.isActiveDocument = false;
      }
    });
  }

  private saveFormDataToAnswers() {
    if (!this.document.answers) {
      return;
    }
    let localAnswers = { ...this.answerData };
    Object.keys(localAnswers)
      .filter((key, val) => !key.endsWith('X'))
      .forEach((key, i, o) => {
        let token = key.slice(0, 7);
        let dimIndex = '';
        if (key.indexOf('[') > -1) {
          token = key.slice(0, 6) + key.slice(-2, -1);
          dimIndex = key.slice(7, -3);
        }

        let answer = this.document?.answers?.find((a) => a.token === token && a.dimIndex === dimIndex);
        // unchecked checkboxes should have value '0' instead of 'false'
        // console.log(key, localAnswers[key]);
        const value = localAnswers[key] !== false ? localAnswers[key] : '0';
        if (value === undefined) {
          return;
        }
        // update documentAnswersMap
        if (this.documentAnswersMap.has(key)) {
          const mapValue = this.documentAnswersMap.get(key);
          if (value !== mapValue) {
            this.documentAnswersMap.set(key, value);
          }
        }
        if (answer) {
          answer.oldAnswer = answer.answer;
          answer.answer = value;
          answer.praSysId = this.document.praSysId;
        } else {
          answer = {
            token: token,
            dimIndex: dimIndex,
            answer: value,
            answerChoice: '',
            oldAnswer: '',
            praSysId: this.document.praSysId,
          };
          this.document?.answers?.push(answer);
        }
      });
  }

  private updateFormDataFromAnswers(answers: Array<AnswerModel>): boolean {
    if (!answers) {
      return false;
    }
    let newAnswerData = { ...this.answerData };
    let updated: boolean = false;
    answers.forEach((a) => {
      let key = this.retrieveKeyOfAnswerMapItemByAnswerObject(a);

      // make sure we don't add any answers for fields that are not in the document
      if (this.documentAnswersMap.has(key)) {
        const oldAnswer = this.documentAnswersMap.get(key);

        if (a.answer !== oldAnswer) {
          // console.log('Answer updated', key, oldAnswer, a.answer);
          this.documentAnswersMap.set(key, a.answer);
          newAnswerData[key] = a.answer;
          updated = true;
        }
      }
    });
    this.answerData = newAnswerData;

    return updated;
  }

  private setOldAnswers(answers: Array<AnswerModel>): Array<AnswerModel> {
    return answers.map((a) => ({
      ...a,
      oldAnswer: a.answer,
    }));
  }

  private getAutosavesByCasId() {
    // we don't want to get the autosaves if we are in a tab with a recovered document
    const recoverSessionId = sessionStorage.getItem('recover_autosave');
    if (this.document.casID && this.document.casID !== '' && (recoverSessionId == null || recoverSessionId === '')) {
      this.autosaveService
        .getByCasId(this.document.casID)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => this.openRecoverDialog(res));
    }
  }

  private openRecoverDialog(autosaves: Array<AutosaveModel>) {
    if (autosaves != null && autosaves.length > 0) {
      const dialog = this.dialogService.openCustomDialog('RecoveredDocumentDialog', RecoverDocumentsDialogComponent, {
        size: 'lg',
        backdrop: 'static',
      });
      dialog.componentInstance.staffID = this.authenticationService.getStaffId();
      dialog.componentInstance.autosaves = autosaves;
      dialog.componentInstance.casID = this.document.casID;
      dialog.componentInstance.casDescription = this.document.casDescription;
      dialog.componentInstance.clientName =
        this.document.ctcLastName && this.document.ctcLastName !== ''
          ? this.document.ctcFirstName + ' ' + this.document.ctcLastName
          : '';
    }
  }

  private getAutosaveBySessionId(autosaveSessionId: string) {
    this.loadingService.setLoadingAction(LoadingAction.Loading);
    this.autosaveService
      .getBySessionId(autosaveSessionId, '')
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.loadingService.resetLoadingDocumentIndicator();
          this.changeDetector.detectChanges();
        })
      )
      .subscribe({
        next: (res) => {
          if (res && res.document) {
            this.document = res;
            this.base64Doc = res.document;
            this.documentDataService.setCasId(this.document.casID ?? '');
            this.documentDataService.setCtcId(this.document.ctcID ?? '');
            this.isRecovered = true;
            this.isDirty = true;
            if (this.isRecovered) {
              this.dialogService.info({
                title: 'Document recovered',
                message: `Document ${this.document.filename} has been recovered.`,
              });
            }
            this.autosaveService
              .create(this.document, '', sessionStorage.getItem('recover_autosave') ?? '')
              .subscribe((res) => {
                sessionStorage.setItem('recover_autosave', this.windowService.getSessionIdFromWindow());
              });
          }
        },
        error: () => {
          this.onFileClicked();
        },
      });
  }

  public printDocument() {
    this.pdfService.print();
  }

  private closeDocument() {
    this.checkUnsavedChanges()
      .pipe(take(1))
      .subscribe((res: any) => {
        if (res === false) {
          return;
        }
        this.autosaveService.deleteBeacon();
        globalThis.PDFViewerApplication.pdfDocument.annotationStorage.resetModified();
        this.isDirty = false;
        this.removeWindowEventListeners();
        this.onlineClientService.disconnectWithLogout();
        window.close();
      });
  }

  private deleteAutosave() {
    const autosaveSessionId = this.document.autosaveSessionId ?? this.windowService.getSessionIdFromWindow();
    this.autosaveService
      .delete(autosaveSessionId ?? '')
      .pipe(take(1))
      .subscribe({
        next: undefined,
        error: undefined,
        complete: () => (this.isRecovered = false),
      });
  }

  private resumeSessionById(sessionId: string, dcmId: string) {
    this.sessionService
      .resumeSession(sessionId, '', dcmId)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoading = false;
          this.changeDetector.detectChanges();
        })
      )
      .subscribe((res) => {
        if (res && res.document) {
          this.document = res;
          this.base64Doc = res.document;
          this.isDirty = res.isDirty ?? false;
          this.checkPendingCaseSessions();
        }
      });
  }

  private resetLoadingDocumentIndicator() {
    this.loadingService.loading = false;
    this.loadingService.loadingAction = LoadingAction.None;
    this.changeDetector.detectChanges();
  }

  public async onPageRendered(event: any) {
    const pageNumber = event.pageNumber;
    const pdfDocument = PDFViewerApplication.pdfDocument;
    const page = await pdfDocument.getPage(pageNumber);
    const viewerContainer = document.getElementById('viewer');
    const annotations = await page.getAnnotations();
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          annotations.forEach((annotation: any) => {
            const element = viewerContainer!.querySelector(`#pdfjs_internal_id_${annotation.id}`);
            if (element) {
              //add listener to set "active field"
              element.addEventListener('focus', this.createFieldEventHandler(annotation) as EventListener);
              observer.disconnect();
            }
          });
        }
      });
    });

    observer.observe(viewerContainer!, { childList: true, subtree: true });
  }

  private createFieldEventHandler(annotation: any) {
    return (event: FocusEvent) => {
      this.onFieldSelected(annotation, event);
    };
  }

  private onFieldSelected(annotation: any, event: any): void {
    //console.log(`selectedField - `, annotation);
    try {
      const activeElement = document.activeElement as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      const fieldDetails: FieldDetails = {
        id: annotation.id,
        name: annotation.fieldName || annotation.id,
        text: activeElement?.value || '',
      };
      // FormField -> Details to match object structure of txt component
      this.activeSelectedField = {
        element: '', //default
        index: 0, //default
        total: 0, //default
        details: fieldDetails,
      };
    } catch (error) {
      console.error('Error in onFieldSelected:', error);
    }
  }

  private checkShowIntroDialog() {
    if (this.authenticationService.getIsFreeTrial()) {
      this.preferencesService
        .getPreference(this.preferencesService.introPopupShowedPreference, this.authenticationService.getInitials())
        .pipe(take(1))
        .subscribe((res) => {
          if (!res || res != '1') {
            this.dialogService.openCustomDialog('Introduction dialog', IntroductionDialogComponent, {
              size: 'md',
              backdrop: 'static',
              centered: true,
            });
          }
        });
    }
  }

  private checkPendingCaseSessions() {
    // Check if we are inside of an iframe. We don't want to open this dialog in an iframe to prevent endless loop
    if (this.document.casID && window.self === window.top) {
      this.sessionService
        .getPendingCaseSessions(this.document.casID)
        .pipe(take(1))
        .subscribe((res) => {
          if (res && res.length > 0) {
            this.hasPendingTakeoverSessions = true;
            const pendingSessionsModalRef = this.dialogService.openCustomDialog(
              'PendingCaseSessionsDialog',
              PendingTakeoverSessionsDialogComponent,
              {
                size: 'xl',
                backdrop: 'static',
                keyboard: false,
                windowClass: 'pending-takeover-modal',
              }
            );

            pendingSessionsModalRef.componentInstance.activeSessions = res;
            pendingSessionsModalRef.componentInstance.casId = this.document.casID;

            pendingSessionsModalRef.result.then((res) => {
              this.hasPendingTakeoverSessions = false;
            });
          }
        });
    }
  }
}
